import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { StatusBadge } from "../../components/common/StatusBadge";
import { FormModal } from "../../components/common/FormModal";
import {
  TextField,
  SelectField,
} from "../../components/common/Form";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  NotificationsService,
  UsersService,
} from "../../services";
import {
  CheckCircle2,
  Plus,
  Pencil,
  Power,
  Search,
  ChevronDown,
  Mail,
  MessageCircle,
  Phone,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useCan } from "../../hooks/useCan";

const empty = {
  userId: "",
  recipientType: "EMAIL",
  recipientValue: "",
  receiveLowStock: true,
  receiveDailyReport: true,
  receiveCashierReminder: true,
  receiveCashSettlement: true,
};

const RECIPIENT_TYPES = ["EMAIL", "WHATSAPP", "SMS"];
const RECIPIENT_TYPE_LABELS = {
  EMAIL: "Email",
  WHATSAPP: "WhatsApp",
  SMS: "SMS",
};
const RECIPIENT_TYPE_ICONS = {
  EMAIL: Mail,
  WHATSAPP: MessageCircle,
  SMS: Phone,
};
const NOTIFICATION_PREFERENCES = [
  { key: "receiveLowStock", label: "Low Stock" },
  { key: "receiveDailyReport", label: "Daily Report" },
  { key: "receiveCashierReminder", label: "Cashier Reminder" },
  { key: "receiveCashSettlement", label: "Settlement" },
];

const PreferencePill = ({ value, label, onClick, disabled = false }) => {
  const enabled = Boolean(value);

  const className = enabled
    ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
    : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100";

  const Icon = enabled ? CheckCircle2 : XCircle;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={
        disabled
          ? `${label}: ${enabled ? "Yes" : "No"}`
          : `${label}: ${enabled ? "Yes" : "No"}. Click to edit.`
      }
      className={`inline-flex items-center justify-center gap-1 rounded-full border px-2 py-1 text-[11px] font-semibold leading-none transition ${className} ${
        disabled ? "cursor-default opacity-80" : "cursor-pointer"
      }`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span>{label}</span>
    </button>
  );
};

export const RecipientsPage = () => {
  const can = useCan();

  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  const load = () => {
    setLoading(true);

    Promise.all([
      NotificationsService.recipients(),
      UsersService.list().catch(() => []),
    ])
      .then(([recipientRows, userRows]) => {
        setRows(recipientRows || []);
        setUsers(userRows || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const getRecipientValueFromUser = (
    userId,
    recipientType
  ) => {
    if (!userId || userId === "__NONE__") {
      return "";
    }

    const selectedUser = users.find(
      (user) => user.userId === Number(userId)
    );

    if (!selectedUser) {
      return "";
    }

    if (recipientType === "EMAIL") {
      return selectedUser.email || "";
    }

    if (recipientType === "WHATSAPP") {
      return selectedUser.whatsappNumber || "";
    }

    if (recipientType === "SMS") {
      return selectedUser.contactNumber || "";
    }

    return "";
  };

  const getAvailableRecipientTypes = (userId, ignoredRecipientId = editingId) => {
    if (!userId || userId === "__NONE__") {
      return RECIPIENT_TYPES;
    }

    const numericUserId = Number(userId);
    const availableTypes = RECIPIENT_TYPES.filter((type) =>
      !rows.some((recipient) =>
        Number(recipient.userId) === numericUserId &&
        recipient.recipientType === type &&
        recipient.recipientId !== ignoredRecipientId
      )
    );

    if (
      editingId &&
      Number(form.userId) === numericUserId &&
      rows.some((recipient) =>
        recipient.recipientId === editingId && recipient.recipientType === form.recipientType
      ) &&
      !availableTypes.includes(form.recipientType)
    ) {
      availableTypes.push(form.recipientType);
    }

    return availableTypes;
  };

  const validate = () => {
    const validationErrors = {};

    if (!form.recipientType) {
      validationErrors.recipientType = "Required";
    }

    if (!form.recipientValue) {
      if (form.userId && form.userId !== "__NONE__") {
        validationErrors.recipientValue =
          "No value added for selected user/channel";
      } else {
        validationErrors.recipientValue = "Required";
      }
    } else if (
      form.recipientType === "EMAIL" &&
      !/^\S+@\S+\.\S+$/.test(form.recipientValue)
    ) {
      validationErrors.recipientValue = "Invalid email";
    } else if (
      (form.recipientType === "WHATSAPP" ||
        form.recipientType === "SMS") &&
      !/^\+?\d{6,15}$/.test(form.recipientValue)
    ) {
      validationErrors.recipientValue = "Invalid number";
    }

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...empty });
    setErrors({});
    setOpen(true);
  };

  const openEdit = (recipient) => {
    setEditingId(recipient.recipientId);

    setForm({
      userId: recipient.userId
        ? String(recipient.userId)
        : "__NONE__",
      recipientType: recipient.recipientType,
      recipientValue: recipient.recipientValue,
      receiveLowStock: Boolean(
        recipient.receiveLowStock
      ),
      receiveDailyReport: Boolean(
        recipient.receiveDailyReport
      ),
      receiveCashierReminder:
        recipient.receiveCashierReminder ?? true,
      receiveCashSettlement:
        recipient.receiveCashSettlement ?? true,
    });

    setErrors({});
    setOpen(true);
  };

  const handleUserChange = (value) => {
    const availableTypes = getAvailableRecipientTypes(value);
    const recipientType = availableTypes.includes(form.recipientType)
      ? form.recipientType
      : availableTypes[0] || "EMAIL";

    setForm({
      ...form,
      userId: value,
      recipientType,
      recipientValue: getRecipientValueFromUser(value, recipientType),
    });
  };

  const submit = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);

    const linkedUserId =
      form.userId && form.userId !== "__NONE__"
        ? Number(form.userId)
        : null;

    const linkedUser = linkedUserId
      ? users.find(
          (user) => user.userId === linkedUserId
        )
      : null;

    const payload = {
      userId: linkedUserId,
      fullName: linkedUser
        ? linkedUser.fullName
        : null,
      recipientType: form.recipientType,
      recipientValue: form.recipientValue,
      receiveLowStock: Boolean(
        form.receiveLowStock
      ),
      receiveDailyReport: Boolean(
        form.receiveDailyReport
      ),
      receiveCashierReminder: Boolean(
        form.receiveCashierReminder
      ),
      receiveCashSettlement: Boolean(
        form.receiveCashSettlement
      ),
    };

    try {
      if (editingId) {
        await NotificationsService.updateRecipient(
          editingId,
          payload
        );
      } else {
        await NotificationsService.createRecipient(
          payload
        );
      }

      toast.success(
        editingId
          ? "Recipient updated"
          : "Recipient added"
      );

      setOpen(false);
      load();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (recipient) => {
    try {
      await NotificationsService.setRecipientStatus(
        recipient.recipientId,
        recipient.status === "ACTIVE"
          ? "INACTIVE"
          : "ACTIVE"
      );

      load();
    } catch {
      toast.error("Failed");
    }
  };

  const groupedRecipients = useMemo(() => {
    const needle = q.toLowerCase().trim();
    const groupsByUser = new Map();

    rows.forEach((recipient) => {
      const key = recipient.userId != null ? `user:${recipient.userId}` : "unlinked";
      if (!groupsByUser.has(key)) {
        groupsByUser.set(key, {
          key,
          userId: recipient.userId,
          fullName: recipient.fullName || recipient.username ||
            (recipient.userId != null ? `User #${recipient.userId}` : "Unlinked Recipients"),
          username: recipient.username || "",
          recipients: [],
        });
      }
      groupsByUser.get(key).recipients.push(recipient);
    });

    return [...groupsByUser.values()]
      .map((group) => {
        const userMatches = needle &&
          `${group.fullName} ${group.username}`.toLowerCase().includes(needle);
        const recipients = !needle || userMatches
          ? group.recipients
          : group.recipients.filter((recipient) =>
              `${recipient.recipientValue || ""} ${recipient.recipientType || ""}`
                .toLowerCase()
                .includes(needle)
            );

        return {
          ...group,
          recipients,
          types: RECIPIENT_TYPES.filter((type) =>
            recipients.some((recipient) => recipient.recipientType === type)
          ),
        };
      })
      .filter((group) => group.recipients.length > 0)
      .sort((first, second) => {
        if (first.key === "unlinked") return 1;
        if (second.key === "unlinked") return -1;
        return first.fullName.localeCompare(second.fullName);
      });
  }, [rows, q]);

  const toggleGroup = (groupKey) => {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const getRecipientMissingMessage = () => {
    if (
      !form.userId ||
      form.userId === "__NONE__"
    ) {
      return "";
    }

    if (form.recipientValue) {
      return "";
    }

    if (form.recipientType === "EMAIL") {
      return "No email address added for selected user.";
    }

    if (form.recipientType === "WHATSAPP") {
      return "No WhatsApp number added for selected user.";
    }

    if (form.recipientType === "SMS") {
      return "No SMS/contact number added for selected user.";
    }

    return "";
  };

  const isLinkedUserSelected =
    form.userId &&
    form.userId !== "__NONE__";

  const canEditPreferences = Boolean(can.manageNotifications);

  return (
    <div>
      <PageHeader
        title="Notification Recipients"
        subtitle="Manage recipients for daily reports, low-stock alerts, cashier reminders and settlement alerts."
        actions={
          can.manageNotifications && (
            <Button
              onClick={openCreate}
              className="bg-navy-800 hover:bg-navy-900 text-white"
              data-testid="recipient-add"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Recipient
            </Button>
          )
        }
      />

      <div className="mb-4 max-w-sm relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />

        <Input
          placeholder="Search address / user / type"
          value={q}
          onChange={(event) =>
            setQ(event.target.value)
          }
          className="pl-9"
          data-testid="recipient-search"
        />
      </div>

      <DataTable
        paginationResetKey={q}
        loading={loading}
        rows={groupedRecipients}
        empty={q ? "No recipients match your search." : "No recipients yet."}
        testId="recipients-table"
        columns={[
          {
            key: "userGroup",
            header: "Users",
            render: (group) => {
              const expanded = expandedGroups.has(group.key) || Boolean(q.trim());
              const initials = group.fullName
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part[0])
                .join("")
                .toUpperCase();
              return (
                <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <button type="button" onClick={() => toggleGroup(group.key)} aria-expanded={expanded} className={`flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2 text-left transition-colors hover:bg-slate-50 sm:px-4 ${expanded ? "bg-slate-50/80" : "bg-white"}`} data-testid={`recipient-group-${group.key}`}>
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-navy-700 text-[11px] font-semibold text-white" aria-hidden="true">
                        {initials || "?"}
                      </span>
                      <span className="truncate font-semibold text-navy-900">{group.fullName}</span>
                      <span className="shrink-0 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-500">
                        {group.recipients.length} {group.recipients.length === 1 ? "recipient" : "recipients"}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="flex flex-wrap justify-end gap-1.5">
                        {group.types.map((type) => {
                          const ChannelIcon = RECIPIENT_TYPE_ICONS[type];
                          return (
                            <span key={type} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-600">
                              {ChannelIcon && <ChannelIcon className="h-3 w-3 text-slate-400" />}
                              {RECIPIENT_TYPE_LABELS[type] || type}
                            </span>
                          );
                        })}
                      </span>
                      <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${expanded ? "" : "-rotate-90"}`} />
                    </span>
                  </button>
                  {expanded && (
                    <div className="divide-y divide-slate-100 border-t border-slate-200 bg-white">
                      {group.recipients.map((recipient) => (
                        <div key={recipient.recipientId} className="grid gap-2.5 px-3 py-2.5 sm:px-4 lg:grid-cols-[minmax(170px,1fr)_minmax(0,2fr)_auto] lg:items-center">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            {(() => {
                              const ChannelIcon = RECIPIENT_TYPE_ICONS[recipient.recipientType];
                              return (
                                <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                                  {ChannelIcon && <ChannelIcon className="h-3 w-3 text-slate-400" />}
                                  {RECIPIENT_TYPE_LABELS[recipient.recipientType] || recipient.recipientType}
                                </span>
                              );
                            })()}
                            <span className="min-w-0 break-all text-sm font-medium text-slate-800">{recipient.recipientValue}</span>
                          </div>
                          <div className="min-w-0">
                            <div className="flex flex-wrap gap-1.5">
                              {NOTIFICATION_PREFERENCES.map((preference) => <PreferencePill key={preference.key} value={recipient[preference.key]} label={preference.label} onClick={() => openEdit(recipient)} disabled={!canEditPreferences} />)}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5 lg:justify-end">
                            <StatusBadge value={recipient.status} />
                            {can.manageNotifications && (
                              <>
                                <Button variant="outline" size="sm" onClick={() => openEdit(recipient)} aria-label={`Edit ${RECIPIENT_TYPE_LABELS[recipient.recipientType] || recipient.recipientType} recipient`} title="Edit" data-testid={`recipient-edit-${recipient.recipientId}`}><Pencil className="h-3.5 w-3.5" /></Button>
                                <Button variant="outline" size="sm" onClick={() => toggle(recipient)} aria-label={`${recipient.status === "ACTIVE" ? "Disable" : "Enable"} recipient`} title={recipient.status === "ACTIVE" ? "Disable" : "Enable"} data-testid={`recipient-toggle-${recipient.recipientId}`}><Power className="h-3.5 w-3.5" /></Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            },
          },
        ]}
      />
      <FormModal
        open={open}
        onOpenChange={setOpen}
        title={
          editingId
            ? "Edit recipient"
            : "New recipient"
        }
        onSubmit={submit}
        loading={saving}
        submitText={
          editingId ? "Save" : "Create"
        }
        description="A recipient can receive daily reports, low-stock alerts, cashier reminders and settlement alerts. Either link a user account or enter a free-text address."
      >
        <SelectField
          label="Linked user"
          value={form.userId || "__NONE__"}
          onChange={handleUserChange}
          options={[
            {
              value: "__NONE__",
              label: "— none —",
            },
            ...users
              .filter((user) => getAvailableRecipientTypes(String(user.userId)).length > 0)
              .map((user) => ({
              value: String(user.userId),
              label: user.fullName || user.username,
              })),
          ]}
          testId="recipient-user"
        />

        <SelectField
          label="Channel"
          required
          value={form.recipientType}
          onChange={(value) =>
            setForm({
              ...form,
              recipientType: value,
              recipientValue: getRecipientValueFromUser(
                form.userId,
                value
              ),
              receiveCashierReminder:
                value === "SMS"
                  ? form.receiveCashierReminder
                  : false,
            })
          }
          options={getAvailableRecipientTypes(form.userId).map((type) => ({
            value: type,
            label: RECIPIENT_TYPE_LABELS[type],
          }))}
          error={errors.recipientType}
        />

        <TextField
          label={
            form.recipientType === "EMAIL"
              ? "Email address"
              : form.recipientType === "WHATSAPP"
              ? "WhatsApp number"
              : "SMS number"
          }
          required
          value={form.recipientValue}
          onChange={(value) =>
            setForm({
              ...form,
              recipientValue: value,
            })
          }
          error={errors.recipientValue}
          testId="recipient-value"
          disabled={Boolean(
            isLinkedUserSelected
          )}
        />

        {getRecipientMissingMessage() && (
          <p className="text-xs text-rose-600 -mt-2">
            {getRecipientMissingMessage()}
          </p>
        )}

        <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 px-3 py-2">
          <Label className="text-sm">
            Receive low stock alerts
          </Label>

          <Switch
            checked={Boolean(
              form.receiveLowStock
            )}
            onCheckedChange={(value) =>
              setForm({
                ...form,
                receiveLowStock: value,
              })
            }
            data-testid="recipient-receive-low-stock"
          />
        </div>

        <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 px-3 py-2">
          <Label className="text-sm">
            Receive daily report
          </Label>

          <Switch
            checked={Boolean(
              form.receiveDailyReport
            )}
            onCheckedChange={(value) =>
              setForm({
                ...form,
                receiveDailyReport: value,
              })
            }
            data-testid="recipient-receive-daily-report"
          />
        </div>

        <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 px-3 py-2">
          <div>
            <Label className="text-sm">
              Receive cash settlement alerts
            </Label>

          
          </div>

          <Switch
            checked={Boolean(
              form.receiveCashSettlement
            )}
            onCheckedChange={(value) =>
              setForm({
                ...form,
                receiveCashSettlement: value,
              })
            }
            data-testid="recipient-receive-cash-settlement"
          />
        </div>

        {form.recipientType === "SMS" && (
          <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 px-3 py-2">
            <div>
              <Label className="text-sm">
                Receive cashier reminders
              </Label>

              <p className="text-xs text-slate-500 mt-0.5">
                Opening reminder at 10:30 AM and closing reminder at 1:00 AM.
              </p>
            </div>

            <Switch
              checked={Boolean(form.receiveCashierReminder)}
              onCheckedChange={(value) =>
                setForm({
                  ...form,
                  receiveCashierReminder: value,
                })
              }
              data-testid="recipient-receive-cashier-reminder"
            />
          </div>
        )}
      </FormModal>
    </div>
  );
};
